/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useWtrSelector, useWtrDispatch } from 'redux/hooks';
import classNames from 'classnames';

import {
  Information,
  Pound,
  SuccessSolid,
} from '@johnlewispartnership/wtr-ingredients/foundations/icons';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { resetError } from 'redux/modules/search-and-browse/actions/reset-error';
import ProductGrid from 'components/ProductGrid/ProductGrid';
import { SSRMediaQuery } from 'components/SSRMediaQuery';
import { BREAKPOINTS } from 'constants/grid';
import ProductSelectMobileScreen from 'components/Builder/ProductSelectMobileScreen';
import SearchAndBrowseLoadMoreButton from 'components/LoadMoreButton';
import { LegacyProductReference } from 'constants/products';

import { ListAnalytics } from 'analytics/types';

import DealBuilder from './DealBuilder';
import { DealBuilderContext } from './context';
import { BaseGroupItemPlaceholder } from './types';
import { getBuilderGroupsLength } from './selectors';
import styles from './styles.scss';

export interface CountAndTotal {
  totalCount: number;
  productCount: number;
}

interface BuilderProps {
  alwaysOpen?: boolean;
  buildersLength: number;
  className?: string;
  getProducts: (index: number) => LegacyProductReference[];
  GroupItemPlaceholder: BaseGroupItemPlaceholder;
  listDefinitions: ListAnalytics;
  loading?: boolean;
  location: string;
  searchType: string;
  setProductCountAndTotal?: React.Dispatch<React.SetStateAction<CountAndTotal | null>>;
  taxonomyLevel: string;
  warning?: string;
  completeMessage?: string;
  initialMessage?: string;
  mobileHeader?: React.ReactElement<any> | null;
  multipleMessage?: string;
  completeIcon?: React.ReactElement<any>;
  defaultIcon?: React.ReactElement<any>;
  promotionId?: string;
  variant: 'bundle' | 'meal deal' | 'offer' | 'wine';
}

export type { BuilderProps };

const defaultCompleteIcon = (
  <SuccessSolid size="large" data-testid="completed" className={styles.successIcon} />
);
const defaultValueIcon = <Pound size="large" className={styles.defaultIcon} />;

const Builder = ({
  alwaysOpen = false,
  buildersLength,
  className,
  location,
  getProducts,
  GroupItemPlaceholder,
  listDefinitions,
  loading = false,
  completeMessage,
  initialMessage = '',
  mobileHeader = null,
  multipleMessage,
  completeIcon = defaultCompleteIcon,
  defaultIcon = defaultValueIcon,
  searchType,
  setProductCountAndTotal,
  taxonomyLevel,
  warning,
  promotionId,
  variant,
}: BuilderProps) => {
  const dispatch = useWtrDispatch();
  const resetErrorCb = useCallback(() => dispatch(resetError()), [dispatch]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);

  const groupsLength = useWtrSelector(state =>
    getBuilderGroupsLength(state, { promotionId, variant }),
  );
  const dismissProductsScreen = useCallback(() => {
    setProductCountAndTotal?.(null);
    setSelectedGroup(null);
  }, [setProductCountAndTotal]);

  const focusTargetIdRef = useRef<string | null>(null);

  const handleFocusOnModalDismiss = useCallback(() => {
    const fallbackElement: HTMLElement | null = document.getElementById('builder-scroll-container');
    const element: HTMLElement | null = focusTargetIdRef.current
      ? document.querySelector(`[data-lineitem="${focusTargetIdRef.current}"]`)
      : null;

    fallbackElement?.focus();

    if (element) {
      setTimeout(() => {
        element.focus();
        focusTargetIdRef.current = null;
      }, 1);
    }
  }, []);

  const setModalDismissFocusTargetId = useCallback((id: string | null) => {
    focusTargetIdRef.current = id;
  }, []);

  const mobileBuilderHandlers = useMemo(
    () => ({
      setModalDismissFocusTargetId,
      setSelectedGroup,
      setProductCountAndTotal,
      dismissProductsScreen,
    }),
    [
      setModalDismissFocusTargetId,
      setSelectedGroup,
      setProductCountAndTotal,
      dismissProductsScreen,
    ],
  );

  const dealBuilder = useMemo(() => {
    return (
      <DealBuilderContext.Provider value={mobileBuilderHandlers}>
        {Array.from({ length: buildersLength }).map((_, index) => (
          <DealBuilder
            alwaysOpen={alwaysOpen}
            index={index}
            completeIcon={completeIcon}
            completeMessage={completeMessage}
            defaultIcon={defaultIcon}
            GroupItemPlaceholder={GroupItemPlaceholder}
            initialMessage={initialMessage}
            key={`builder-${index + 1}`}
            length={buildersLength}
            location={location}
            multipleMessage={multipleMessage}
            promotionId={promotionId}
            variant={variant}
            builderId={`builder-${index + 1}`}
          />
        ))}
      </DealBuilderContext.Provider>
    );
  }, [
    GroupItemPlaceholder,
    alwaysOpen,
    buildersLength,
    completeIcon,
    completeMessage,
    defaultIcon,
    initialMessage,
    location,
    mobileBuilderHandlers,
    multipleMessage,
    promotionId,
    variant,
  ]);

  return (
    <>
      <div className={classNames(styles.builderContainer, className)}>
        <div className={styles.scrollContainer} id="builder-scroll-container" tabIndex={-1}>
          {dealBuilder}
          {warning && (
            <div className={styles.cheapestItemWarning}>
              <Information className={styles.icon} />
              <Typography noMargins styleAs="paragraphSmall" className={styles.cheapestItemText}>
                {warning}
              </Typography>
            </div>
          )}
        </div>
      </div>
      <SSRMediaQuery maxWidth={BREAKPOINTS.lg}>
        {Array.from({ length: groupsLength }).map((_e, index) => {
          const key = String(index + 1);
          const products = getProducts(index);

          return (
            <ProductSelectMobileScreen
              key={`product-select-screen-${key}`}
              groupId={key}
              groupIndex={index}
              onClickBack={dismissProductsScreen}
              onDismiss={handleFocusOnModalDismiss}
              initialMessage={initialMessage}
              mobileHeader={mobileHeader}
              promotionId={promotionId}
              variant={variant}
              selectedGroup={selectedGroup}
            >
              <ProductGrid
                products={products}
                loading={loading}
                // @ts-expect-error untyped component
                searchType={searchType}
                // @ts-expect-error untyped component
                taxonomyLevel={taxonomyLevel}
                resetError={resetErrorCb}
                // @ts-expect-error untyped component
                LoadMoreButton={SearchAndBrowseLoadMoreButton}
                listDefinitions={listDefinitions}
              />
            </ProductSelectMobileScreen>
          );
        })}
      </SSRMediaQuery>
    </>
  );
};

Builder.displayName = 'Builder';

export default memo(Builder);
